import { ENTITY_TYPES } from '@/data/constants/constants'

/**
 * Row model for the phone/tablet cash flow table.
 *
 * The arithmetic mirrors TransactionTable.jsx (desktop grid) line for line —
 * vault cells read closing_balance off the period-aggregate feed, category
 * cells read turnover, Money In/Out caps sum turnover by direction, and
 * discrepancy is actual − estimated on periods at or before now. Keep the two
 * in step: a figure must read the same on every device.
 */

export type Tone = 'vault' | 'in' | 'out' | 'neutral'
export type RowKind = 'cap' | 'group' | 'leaf' | 'add' | 'net' | 'closing'

export interface PeriodAggregate {
  offset: number
  vaults?: Record<
    string,
    {
      amount_in?: number
      amount_out?: number
      closing_balance?: number
      estimated_amount_in?: number
      estimated_amount_out?: number
      estimated_closing_balance?: number
    }
  >
  categories?: Record<string, { turnover?: number; estimated_turnover?: number }>
}

export interface TableCell {
  offset: number
  /** null = nothing logged or estimated in this period (rendered as a dash) */
  amount: number | null
  discrepancy: number | null
  /** negative vault balance / net — the desktop grid tints these yellow */
  negative: boolean
}

export type RowTarget =
  | { kind: 'vault'; id: number }
  | { kind: 'category'; id: number; type: 'in' | 'out' }
  | { kind: 'add-vault' }
  | { kind: 'add-category'; type: 'in' | 'out' }

export interface TableRowModel {
  key: string
  kind: RowKind
  tone: Tone
  name: string
  meta?: string
  depth: number
  /** set on collapsible rows (caps and groups) */
  expanded?: boolean
  target?: RowTarget
  /** Money Out rows: over the estimate reads as bad, not good */
  isOut: boolean
  cells: TableCell[]
}

export interface ScopeVault {
  id: number
  name: string
  description?: string
  groupName?: string
}

interface BuildArgs {
  vaults: any[]
  incomeCategories: any[]
  expenseCategories: any[]
  periodByOffset: Map<number, PeriodAggregate>
  offsets: number[]
  /** null = every vault */
  selectedVaultIds: Set<number> | null
  collapsed: Record<string, boolean>
}

const EPSILON = 0.005

export const isArchived = (item: any) =>
  !!(item?.is_archive || item?.is_archived)

const vaultClosing = (period: PeriodAggregate | undefined, id: number) =>
  period?.vaults?.[id]?.closing_balance ?? 0

export function categoryTurnover(
  period: PeriodAggregate | undefined,
  categoryId: number | string,
  estimated: boolean,
) {
  const agg = period?.categories?.[categoryId]
  if (!agg) return 0
  return (estimated ? agg.estimated_turnover : agg.turnover) ?? 0
}

function categoryHasActivity(period: PeriodAggregate | undefined, id: number) {
  const agg = period?.categories?.[id]
  return (
    !!agg && ((agg.turnover ?? 0) !== 0 || (agg.estimated_turnover ?? 0) !== 0)
  )
}

/** category id → direction (+1 in / −1 out), flattening groups */
export function categoryDirections(income: any[], expense: any[]) {
  const map = new Map<number, number>()
  const walk = (list: any[], fallback: number) => {
    ;(list ?? []).forEach((item) => {
      if (item.transaction_categories) {
        walk(item.transaction_categories, item.direction ?? fallback)
      } else {
        map.set(Number(item.id), item.direction ?? fallback)
      }
    })
  }
  walk(income, 1)
  walk(expense, -1)
  return map
}

function headerTurnover(
  period: PeriodAggregate | undefined,
  directions: Map<number, number>,
  wantDirection: number,
  estimated: boolean,
) {
  if (!period) return 0
  return Object.keys(period.categories ?? {}).reduce((acc, id) => {
    if (directions.get(Number(id)) !== wantDirection) return acc
    return acc + categoryTurnover(period, id, estimated)
  }, 0)
}

const discrepancyOf = (offset: number, actual: number, estimated: number) => {
  if (offset > 0) return null
  const d = actual - estimated
  return Math.abs(d) >= EPSILON ? d : null
}

/** Every live vault the scope picker can offer, groups flattened. */
export function scopeVaults(vaults: any[]): ScopeVault[] {
  const out: ScopeVault[] = []
  ;(vaults ?? []).forEach((item) => {
    if (isArchived(item)) return
    if (item.is_group || item.entity_type === ENTITY_TYPES.VAULT_GROUP) {
      ;(item.vaults ?? []).forEach((v) => {
        if (!isArchived(v))
          out.push({
            id: Number(v.id),
            name: v.name,
            description: v.description,
            groupName: item.name,
          })
      })
    } else {
      out.push({
        id: Number(item.id),
        name: item.name,
        description: item.description,
      })
    }
  })
  return out
}

export function buildTableRows({
  vaults,
  incomeCategories,
  expenseCategories,
  periodByOffset,
  offsets,
  selectedVaultIds,
  collapsed,
}: BuildArgs): TableRowModel[] {
  const rows: TableRowModel[] = []
  const periods = offsets.map((o) => periodByOffset.get(Number(o)))
  const directions = categoryDirections(incomeCategories, expenseCategories)
  const isOpen = (key: string) => !collapsed[key]
  const inScope = (id: number) =>
    !selectedVaultIds || selectedVaultIds.has(Number(id))

  const cellsFor = (
    fn: (period: PeriodAggregate | undefined, offset: number) => {
      amount: number | null
      discrepancy?: number | null
      negative?: boolean
    },
  ): TableCell[] =>
    offsets.map((offset, i) => {
      const r = fn(periods[i], offset)
      return {
        offset,
        amount: r.amount,
        discrepancy: r.discrepancy ?? null,
        negative: !!r.negative,
      }
    })

  const vaultCells = (ids: number[]) =>
    cellsFor((period) => {
      const total = ids.reduce((acc, id) => acc + vaultClosing(period, id), 0)
      return { amount: total, negative: total < 0 }
    })

  // ---- Vaults ------------------------------------------------------------
  const visibleVaultIds = scopeVaults(vaults)
    .map((v) => v.id)
    .filter(inScope)

  rows.push({
    key: 'vaults',
    kind: 'cap',
    tone: 'vault',
    name: 'Vaults',
    depth: 0,
    expanded: isOpen('vaults'),
    isOut: false,
    cells: vaultCells(visibleVaultIds),
  })

  if (isOpen('vaults')) {
    ;(vaults ?? []).forEach((item) => {
      if (isArchived(item)) return
      const isGroup =
        item.is_group || item.entity_type === ENTITY_TYPES.VAULT_GROUP

      if (!isGroup) {
        if (!inScope(item.id)) return
        rows.push({
          key: `vault-${item.id}`,
          kind: 'leaf',
          tone: 'neutral',
          name: item.name,
          meta: item.description || undefined,
          depth: 0,
          target: { kind: 'vault', id: Number(item.id) },
          isOut: false,
          cells: vaultCells([Number(item.id)]),
        })
        return
      }

      const members = (item.vaults ?? []).filter(
        (v) => !isArchived(v) && inScope(v.id),
      )
      if (!members.length) return
      const key = `vault-group-${item.id}`
      rows.push({
        key,
        kind: 'group',
        tone: 'vault',
        name: item.name,
        meta: `${members.length} ${members.length === 1 ? 'vault' : 'vaults'}`,
        depth: 0,
        expanded: isOpen(key),
        isOut: false,
        cells: vaultCells(members.map((v) => Number(v.id))),
      })
      if (isOpen(key)) {
        members.forEach((v) =>
          rows.push({
            key: `vault-${v.id}`,
            kind: 'leaf',
            tone: 'neutral',
            name: v.name,
            meta: v.description || undefined,
            depth: 1,
            target: { kind: 'vault', id: Number(v.id) },
            isOut: false,
            cells: vaultCells([Number(v.id)]),
          }),
        )
      }
    })

    rows.push({
      key: 'add-vault',
      kind: 'add',
      tone: 'neutral',
      name: 'Add vault',
      depth: 0,
      target: { kind: 'add-vault' },
      isOut: false,
      cells: [],
    })
  }

  // ---- Money In / Money Out ----------------------------------------------
  const section = (
    key: 'in' | 'out',
    label: string,
    list: any[],
    direction: 1 | -1,
  ) => {
    const isOut = direction === -1
    const tone: Tone = isOut ? 'out' : 'in'

    rows.push({
      key,
      kind: 'cap',
      tone,
      name: label,
      depth: 0,
      expanded: isOpen(key),
      isOut,
      cells: cellsFor((period, offset) => {
        const actual = headerTurnover(period, directions, direction, false)
        const estimated = headerTurnover(period, directions, direction, true)
        return {
          amount: actual,
          discrepancy: discrepancyOf(offset, actual, estimated),
        }
      }),
    })
    if (!isOpen(key)) return

    const leafRow = (cat: any, depth: number): TableRowModel => ({
      key: `category-${cat.id}`,
      kind: 'leaf',
      tone,
      name: cat.name,
      meta: cat.description || undefined,
      depth,
      target: { kind: 'category', id: Number(cat.id), type: key },
      isOut,
      cells: cellsFor((period, offset) => {
        if (!categoryHasActivity(period, cat.id)) {
          return { amount: null }
        }
        const actual = categoryTurnover(period, cat.id, false)
        const estimated = categoryTurnover(period, cat.id, true)
        return {
          amount: actual,
          discrepancy: discrepancyOf(offset, actual, estimated),
        }
      }),
    })

    ;(list ?? []).forEach((item) => {
      if (isArchived(item)) return
      if (!item.transaction_categories) {
        rows.push(leafRow(item, 0))
        return
      }

      const members = item.transaction_categories ?? []
      const groupKey = `category-group-${item.id}`
      const live = members.filter((c) => !isArchived(c))
      rows.push({
        key: groupKey,
        kind: 'group',
        tone,
        name: item.name,
        meta: `${live.length} ${live.length === 1 ? 'category' : 'categories'}`,
        depth: 0,
        expanded: isOpen(groupKey),
        isOut,
        cells: cellsFor((period, offset) => {
          if (!members.some((c) => categoryHasActivity(period, c.id))) {
            return { amount: null }
          }
          const sum = (estimated: boolean) =>
            members.reduce(
              (acc, c) => acc + categoryTurnover(period, c.id, estimated),
              0,
            )
          const actual = sum(false)
          return {
            amount: actual,
            discrepancy: discrepancyOf(offset, actual, sum(true)),
          }
        }),
      })
      if (isOpen(groupKey)) live.forEach((c) => rows.push(leafRow(c, 1)))
    })

    rows.push({
      key: `add-${key}`,
      kind: 'add',
      tone,
      name: 'Add category',
      depth: 0,
      target: { kind: 'add-category', type: key },
      isOut,
      cells: [],
    })
  }

  section('in', 'Money In', incomeCategories, 1)
  section('out', 'Money Out', expenseCategories, -1)

  // ---- Totals -------------------------------------------------------------
  rows.push({
    key: 'net',
    kind: 'net',
    tone: 'neutral',
    name: 'Net movement',
    depth: 0,
    isOut: false,
    cells: cellsFor((period) => {
      const net =
        headerTurnover(period, directions, 1, false) -
        headerTurnover(period, directions, -1, false)
      return { amount: net, negative: net < 0 }
    }),
  })

  rows.push({
    key: 'closing',
    kind: 'closing',
    tone: 'neutral',
    name: 'Closing balance',
    depth: 0,
    isOut: false,
    cells: vaultCells(visibleVaultIds),
  })

  return rows
}
